import { useState, useRef, useEffect } from 'react';
import api from '../api';
import { useMsal } from '@azure/msal-react';

const FeedbackForm = ({ expenseId, onClose, initialRating = 0, initialRemarks = '', isUpdate = false }) => {
    const [rating, setRating] = useState(initialRating || 0);
    const [remarks, setRemarks] = useState(initialRemarks || '');
    const [files, setFiles] = useState([]); // support multiple images
    const [recommendVendor, setRecommendVendor] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hover, setHover] = useState(0);
    const fileInputRef = useRef(null);

    const { instance, accounts } = useMsal();

    useEffect(() => {
        setRating(initialRating || 0);
        setRemarks(initialRemarks || '');
    }, [initialRating, initialRemarks]);

    const handleFileChange = (e) => {
        const list = Array.from(e.target.files || []);
        if (list.length) {
            setFiles(list);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();  // Move to top
        if (!rating) return;

        try {
            // Get fresh token
            const result = await instance.acquireTokenSilent({
                scopes: ["https://graph.microsoft.com/.default"],
                account: accounts[0],
            }).catch(async (error) => {
                console.error('Token acquisition failed:', error);
                // Try to get token interactively if silent fails
                return await instance.acquireTokenPopup({
                    scopes: ["https://graph.microsoft.com/.default"]
                });
            });

            const formData = new FormData();
            formData.append('expense_id', expenseId);
            formData.append('rating', rating);
            // Backend accepts both 'remarks' and 'comments'; send 'remarks'
            if (remarks) formData.append('remarks', remarks);
            formData.append('recommend_vendor', recommendVendor);
            formData.append('accessToken', result.accessToken);

            // Support multiple files: append under 'files'
            if (files && files.length) {
                files.forEach(f => formData.append('files', f));
            }

            setIsSubmitting(true);
            const url = isUpdate
                ? `/api/feedback/${encodeURIComponent(expenseId)}`
                : '/api/feedback';
            const method = isUpdate ? 'put' : 'post';
            const response = await api({
                url,
                method,
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Show success message
            alert(isUpdate ? 'Feedback updated successfully!' : 'Feedback submitted successfully!');
            onClose(true);
            setFiles([]);

        } catch (error) {
            console.error('Error submitting feedback:', error);
            alert(`Failed to submit feedback: ${error.response?.data?.error || error.message || 'Please try again'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center p-4 z-50" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="p-6 rounded-xl w-full max-w-lg shadow-2xl border" style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--text)' }}>
                <div className="mb-1 flex items-baseline justify-between">
                    <h3 className="text-xl font-semibold">{isUpdate ? 'Update Feedback' : 'Goods Received Feedback'}</h3>
                    <button type="button" className="rounded-md px-2" style={{ color: 'var(--muted)' }} onClick={() => onClose(false)}>✕</button>
                </div>
                <p className="text-xs mb-4" style={{ color: 'var(--muted)' }}>Expense ID: <span className="font-mono" style={{ color: 'var(--text)' }}>{expenseId}</span></p>
                <form onSubmit={handleSubmit}>

                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text)' }}>
                            Rating
                        </label>
                        <div className="flex space-x-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    className={`text-2xl leading-none ${star <= (hover || rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                                    onMouseEnter={() => setHover(star)}
                                    onMouseLeave={() => setHover(0)}
                                    onClick={() => setRating(star)}
                                >
                                    ★
                                </button>
                            ))}
                            <span className="ml-2 text-xs" style={{ color: 'var(--muted)' }}>{rating ? `${rating}/5` : 'Select a rating'}</span>
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text)' }}>
                            Remarks (Optional)
                        </label>
                        <textarea
                            className="w-full p-3 rounded-md focus:outline-none"
                            style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                            rows="3"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            placeholder="Notes on quality, quantity, issues, etc."
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text)' }}>
                            Upload goods received photos (Optional)
                        </label>
                        <div className="mt-1 flex items-center">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                                multiple
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current.click()}
                                className="py-2 px-3 rounded-md shadow-sm text-sm leading-4 font-medium focus:outline-none"
                                style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                            >
                                Choose File
                            </button>
                            <span className="ml-2 text-sm" style={{ color: 'var(--muted)' }}>
                                {files && files.length ? `${files.length} file(s) selected` : 'No files chosen'}
                            </span>
                        </div>
                        {files && files.length > 0 && (
                            <div className="mt-3 grid grid-cols-3 gap-2">
                                {files.map((f, idx) => (
                                    <img
                                        key={idx}
                                        src={URL.createObjectURL(f)}
                                        alt={`Preview ${idx + 1}`}
                                        className="h-24 w-full object-cover rounded"
                                        style={{ border: '1px solid var(--border)' }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="mb-4 flex items-center gap-2">
                        <input
                            id="recommendVendor"
                            type="checkbox"
                            checked={recommendVendor}
                            onChange={(e) => setRecommendVendor(e.target.checked)}
                            className="h-4 w-4 rounded"
                            style={{ accentColor: 'var(--primary)', border: '1px solid var(--border)' }}
                        />
                        <label htmlFor="recommendVendor" className="text-sm" style={{ color: 'var(--text)' }}>
                            Recommend approaching this vendor again
                        </label>
                    </div>

                    <div className="flex justify-end space-x-2 pt-2">
                        <button
                            type="button"
                            onClick={() => onClose(false)}
                            className="px-4 py-2 text-sm font-medium rounded border"
                            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!rating || isSubmitting}
                            className={`px-4 py-2 text-sm font-medium text-white rounded ${!rating || isSubmitting ? 'cursor-not-allowed opacity-60' : ''}`}
                            style={{ background: '#8c211e', border: '1px solid #721a18' }}
                        >
                            {isSubmitting ? (isUpdate ? 'Updating...' : 'Submitting...') : (isUpdate ? 'Update Feedback' : 'Submit Feedback')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FeedbackForm;